/**
 * Mobile menu functionality
 * Handles hamburger menu toggle and search toggle
 * @package Felix
 */

// Module-level references (set during init)
let headerEl = null
let menuToggleEl = null

/**
 * Track whether the announcement bar is currently visible in viewport.
 * Used by fixed overlays that need to sit below it only while visible.
 */
function initAnnouncementBarState() {
	const announcementBar = document.querySelector('.announcement-bar__container')

	// Ensure stale class is cleared when bar is not rendered.
	if (!announcementBar) {
		document.documentElement.style.setProperty('--felix-announcement-bar-visible-height', '0px')
		return
	}

	const updateBarState = () => {
		const rect = announcementBar.getBoundingClientRect()
		const visibleHeight = Math.max(0, Math.min(rect.bottom, rect.height))
		document.documentElement.style.setProperty('--felix-announcement-bar-visible-height', `${visibleHeight}px`)
	}

	window.addEventListener('scroll', updateBarState, { passive: true })
	window.addEventListener('resize', updateBarState, { passive: true })
	updateBarState()
}

/**
 * Open mobile menu
 */
function openMobileMenu() {
	if (!headerEl || !menuToggleEl) return

	headerEl.classList.add('main-header--menu-open')
	document.body.classList.add('mobile-menu-open')
	menuToggleEl.setAttribute('aria-expanded', 'true')
}

/**
 * Close mobile menu
 */
function closeMobileMenu() {
	if (!headerEl || !menuToggleEl) return

	headerEl.classList.remove('main-header--menu-open')
	document.body.classList.remove('mobile-menu-open')
	menuToggleEl.setAttribute('aria-expanded', 'false')

	// Reset all aria-expanded on Level 1 links
	const level1Links = document.querySelectorAll('.megamenu-l1__link[aria-expanded]')
	level1Links.forEach((link) => {
		link.setAttribute('aria-expanded', 'false')
	})
}

/**
 * Check if mobile menu is open
 */
function isMobileMenuOpen() {
	return headerEl?.classList.contains('main-header--menu-open') ?? false
}

export function initMobileMenu() {
	headerEl = document.querySelector('.main-header')
	menuToggleEl = document.querySelector('.main-header__mobile-menu-toggle')
	const searchToggle = document.querySelector('.main-header__mobile-search-toggle')
	const menu = document.querySelector('.main-header__menu')

	initAnnouncementBarState()

	if (!menuToggleEl || !menu || !headerEl) {
		return
	}

	// Toggle mobile menu
	menuToggleEl.addEventListener('click', () => {
		if (isMobileMenuOpen()) {
			closeMobileMenu()
		} else {
			openMobileMenu()
		}
	})

	// Close buttons inside megamenus (data-close-mobile-menu)
	// Uses delegated listener so it works for dynamically restructured menus
	document.addEventListener('click', (e) => {
		const closeBtn = e.target.closest('[data-close-mobile-menu]')
		if (!closeBtn) return

		// Reset all megamenu states (Level 3 → Level 2 → mobile menu)
		document.querySelectorAll('.megamenu-l3__list.is-active').forEach((sub) => sub.classList.remove('is-active'))

		document.querySelectorAll('.megamenu.is-active').forEach((mega) => {
			mega.classList.remove('is-active')
			const parentLink = mega.closest('.megamenu-l1__item')?.querySelector('.megamenu-l1__link')
			if (parentLink) parentLink.setAttribute('aria-expanded', 'false')
		})

		// Show all Level 1 items again
		const mainMenuList = document.querySelector('.main-header__mobile-menu')
		if (mainMenuList) {
			mainMenuList.querySelectorAll(':scope > .megamenu-l1__item').forEach((item) => {
				item.style.display = ''
			})
		}

		closeMobileMenu()
	})

	// Handle search icon click
	if (searchToggle) {
		searchToggle.addEventListener('click', () => {
			openMobileMenu()
			document.dispatchEvent(new CustomEvent('quick-search:open'))
		})
	}

	// Close menu when clicking on Level 3 links (actual destinations)
	const navLinks = menu.querySelectorAll('a')
	navLinks.forEach((link) => {
		link.addEventListener('click', () => {
			// Only close for Level 3 links (inside megamenu-l3__list)
			const isLevel3Link = link.closest('.megamenu-l3__list')

			if (isLevel3Link) {
				closeMobileMenu()
			}
		})
	})

	// Close menu on escape key
	document.addEventListener('keydown', (e) => {
		if (e.key === 'Escape' && isMobileMenuOpen()) {
			// Close any open Level 3 submenus first
			const activeSubmenus = document.querySelectorAll('.megamenu-l3__list.is-active')
			if (activeSubmenus.length > 0) {
				activeSubmenus.forEach((submenu) => {
					submenu.classList.remove('is-active')
					// Show Level 2 content
					const megamenu = submenu.closest('.megamenu')
					if (megamenu) {
						const columns = megamenu.querySelector('.megamenu__columns')
						const cta = megamenu.querySelector('.megamenu__cta')
						const backBtn = megamenu.querySelector('.megamenu__back')
						if (columns) columns.style.display = ''
						if (cta) cta.style.display = ''
						if (backBtn) backBtn.style.display = ''
					}
				})
				return
			}

			// Close any open Level 2 megamenus
			const activeMegamenus = document.querySelectorAll('.megamenu.is-active')
			if (activeMegamenus.length > 0) {
				activeMegamenus.forEach((megamenu) => {
					// Update aria-expanded on Level 1 link
					const parentItem = megamenu.closest('.megamenu-l1__item')
					const parentLink = parentItem?.querySelector('.megamenu-l1__link')
					if (parentLink) {
						parentLink.setAttribute('aria-expanded', 'false')
					}

					megamenu.classList.remove('is-active')
					// Show all Level 1 items
					const mainMenuList = megamenu.closest('.main-header__menu')
					if (mainMenuList) {
						const allLevel1Items = mainMenuList.querySelectorAll(':scope > .megamenu-l1__item')
						allLevel1Items.forEach((item) => {
							item.style.display = ''
						})
					}
				})
				return
			}

			// Close hamburger menu
			closeMobileMenu()
			menuToggleEl.focus()
		}
	})
}
